/**
 * CompsList Component
 * Displays a list of film comps with filtering and sorting options
 */

import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button
} from '@mui/material';
import { 
  Sort, 
  FilterList,
  Refresh,
  ViewList,
  ViewModule,
  Add
} from '@mui/icons-material';
import CompCard from './CompCard';

const CompsList = ({ 
  comps, 
  loading = false, 
  onRefresh,
  canManage = false,
  userProfile = null,
  onEdit = null,
  onDelete = null,
  onViewDetails = null,
  onAddComp = null
}) => {
  const [sortBy, setSortBy] = useState('date');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Combine all comps for display (only greenlight comps now)
  // NOTE: Modeled comps have been temporarily removed.
  // They may be re-added in the future when the model improves.
  const allComps = useMemo(() => {
    const combined = [];
    
    // Add greenlight comps only
    if (comps.greenlight && Array.isArray(comps.greenlight)) {
      combined.push(...comps.greenlight.map(comp => ({ ...comp, compType: 'greenlight' })));
    }
    
    return combined;
  }, [comps]);

  // Sort comps
  const sortedComps = useMemo(() => {
    const sorted = [...allComps];
    
    switch (sortBy) {
      case 'title':
        return sorted.sort((a, b) => (a.compFilmTitle || '').localeCompare(b.compFilmTitle || ''));
      case 'date':
        return sorted.sort((a, b) => {
          const dateA = new Date(a.assignedAt || 0);
          const dateB = new Date(b.assignedAt || 0);
          return dateB - dateA;
        });
      default:
        return sorted;
    }
  }, [allComps, sortBy]);

  // Filter comps (no filtering needed since we only have greenlight comps)
  const filteredComps = sortedComps;

  // Handle sort change
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };


  // Handle view mode toggle
  const handleViewModeToggle = () => {
    setViewMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Empty state
  if (filteredComps.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
          No comps found
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No film comparisons are available for this film yet.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="outlined" 
            startIcon={<Refresh />}
            onClick={onRefresh}
          >
            Refresh
          </Button>
          {canManage && onAddComp && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={onAddComp}
            >
              Add Greenlight Comp
            </Button>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          {/* Left side - Filters and Sort */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={handleSortChange}
                startAdornment={<Sort sx={{ fontSize: '1rem', mr: 1, color: 'text.secondary' }} />}
              >
                <MenuItem value="title">Title</MenuItem>
                <MenuItem value="date">Date</MenuItem>
              </Select>
            </FormControl>

            <Chip 
              label={`${filteredComps.length} comp${filteredComps.length !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>

          {/* Right side - View mode and refresh */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              size="small"
              variant={viewMode === 'grid' ? 'contained' : 'outlined'}
              onClick={handleViewModeToggle}
              startIcon={viewMode === 'grid' ? <ViewModule /> : <ViewList />}
            >
              {viewMode === 'grid' ? 'Grid' : 'List'}
            </Button>
            
            <Button
              size="small"
              variant="outlined"
              startIcon={<Refresh />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Comps Grid/List */}
      <Grid container spacing={2}>
        {filteredComps.map((comp, index) => (
          <Grid 
            item 
            xs={12} 
            sm={viewMode === 'grid' ? 6 : 12} 
            md={viewMode === 'grid' ? 4 : 12} 
            lg={viewMode === 'grid' ? 3 : 12}
            key={comp.id || `${comp.compType}-${index}`}
          >
            <CompCard
              comp={comp}
              type={comp.compType}
              onEdit={canManage && onEdit ? () => onEdit(comp) : null}
              onDelete={canManage && onDelete ? () => onDelete(comp) : null}
              onViewDetails={onViewDetails ? () => onViewDetails(comp) : null}
              viewMode={viewMode}
            />
          </Grid>
        ))}
      </Grid>

      {/* Load more button (for future pagination) */}
      {filteredComps.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredComps.length} of {allComps.length} comps
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CompsList;
